import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const ROUND_KEY_VALUES = ['preliminary', 'final'] as const
export const roundKeySchema = z.enum(ROUND_KEY_VALUES)
export type RoundKey = z.infer<typeof roundKeySchema>

export const ROUND_STATUS_VALUES = ['draft', 'open', 'judging', 'closed'] as const
export const roundStatusSchema = z.enum(ROUND_STATUS_VALUES)
export type RoundStatus = z.infer<typeof roundStatusSchema>

/**
 * Who may contribute scores for a round. `judge_only` is the default and the
 * behavior every round had before manual input existed.
 */
export const SCORING_INPUT_MODE_VALUES = ['judge_only', 'staff_only', 'judge_and_staff'] as const
export const scoringInputModeSchema = z.enum(SCORING_INPUT_MODE_VALUES)
export type ScoringInputMode = z.infer<typeof scoringInputModeSchema>

/** Whether judges score the round under this mode. */
export function modeAllowsJudges(mode: ScoringInputMode): boolean {
  return mode !== 'staff_only'
}

/** Whether staff may enter manual values for the round under this mode. */
export function modeAllowsStaff(mode: ScoringInputMode): boolean {
  return mode !== 'judge_only'
}

// ---------------------------------------------------------------------------
// Cross-field schedule rule, shared by the create request and by the merged
// record re-checked on PATCH (see `mergedRoundRulesSchema`) — mirrors
// `addScheduleIssues` in `competition.ts`, but a round's own timeline is the
// full 5-link chain (registration → submission → judging → result), not just
// three independent start/end windows: it is what admins now edit directly
// per round instead of a competition-level flat schedule (UX-112).
// ---------------------------------------------------------------------------

interface RoundSchedulePairs {
  registrationStartAt?: string | null
  registrationEndAt?: string | null
  submissionStartAt?: string | null
  submissionEndAt?: string | null
  judgingStartAt?: string | null
  judgingEndAt?: string | null
  resultPublishAt?: string | null
}

const ROUND_SCHEDULE_CHAIN = [
  ['registrationStartAt', 'registrationEndAt'],
  ['submissionStartAt', 'submissionEndAt'],
  ['submissionEndAt', 'judgingStartAt'],
  ['judgingStartAt', 'judgingEndAt'],
  ['judgingEndAt', 'resultPublishAt'],
] as const

function addRoundScheduleIssues(value: RoundSchedulePairs, ctx: z.RefinementCtx): void {
  for (const [beforeKey, afterKey] of ROUND_SCHEDULE_CHAIN) {
    const before = value[beforeKey]
    const after = value[afterKey]
    if (before != null && after != null && Date.parse(after) < Date.parse(before)) {
      ctx.addIssue({
        code: 'custom',
        path: [afterKey],
        message: `${afterKey} must not be before ${beforeKey}.`,
      })
    }
  }
}

export const roundSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  roundOrder: z.int().min(1),
  key: roundKeySchema,
  name: z.string().trim().min(1).max(120),
  status: roundStatusSchema,
  scoringInputMode: scoringInputModeSchema,
  registrationStartAt: utcTimestampSchema.nullable(),
  registrationEndAt: utcTimestampSchema.nullable(),
  submissionStartAt: utcTimestampSchema.nullable(),
  submissionEndAt: utcTimestampSchema.nullable(),
  judgingStartAt: utcTimestampSchema.nullable(),
  judgingEndAt: utcTimestampSchema.nullable(),
  resultPublishAt: utcTimestampSchema.nullable(),
  submissionFormId: publicIdSchema.nullable(),
  defaultRubricId: publicIdSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type Round = z.infer<typeof roundSchema>

export const roundDtoSchema = roundSchema.omit({ competitionId: true })
export type RoundDto = z.infer<typeof roundDtoSchema>

export const createRoundRequestSchema = z
  .strictObject({
    key: roundKeySchema,
    name: z.string().trim().min(1).max(120),
    scoringInputMode: scoringInputModeSchema.default('judge_only'),
    registrationStartAt: utcTimestampSchema.nullable().default(null),
    registrationEndAt: utcTimestampSchema.nullable().default(null),
    submissionStartAt: utcTimestampSchema.nullable().default(null),
    submissionEndAt: utcTimestampSchema.nullable().default(null),
    judgingStartAt: utcTimestampSchema.nullable().default(null),
    judgingEndAt: utcTimestampSchema.nullable().default(null),
    resultPublishAt: utcTimestampSchema.nullable().default(null),
    submissionFormId: publicIdSchema.nullable().default(null),
    defaultRubricId: publicIdSchema.nullable().default(null),
  })
  .superRefine(addRoundScheduleIssues)
export type CreateRoundRequest = z.infer<typeof createRoundRequestSchema>

/**
 * Every field optional, but at least one required — a PATCH with an empty body
 * would otherwise write an audit entry recording that nothing changed.
 */
export const updateRoundRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    status: roundStatusSchema.optional(),
    scoringInputMode: scoringInputModeSchema.optional(),
    registrationStartAt: utcTimestampSchema.nullable().optional(),
    registrationEndAt: utcTimestampSchema.nullable().optional(),
    submissionStartAt: utcTimestampSchema.nullable().optional(),
    submissionEndAt: utcTimestampSchema.nullable().optional(),
    judgingStartAt: utcTimestampSchema.nullable().optional(),
    judgingEndAt: utcTimestampSchema.nullable().optional(),
    resultPublishAt: utcTimestampSchema.nullable().optional(),
    submissionFormId: publicIdSchema.nullable().optional(),
    defaultRubricId: publicIdSchema.nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
export type UpdateRoundRequest = z.infer<typeof updateRoundRequestSchema>

/**
 * Re-runs the schedule chain over an already-merged round record. A PATCH
 * that moves only `submissionEndAt` can still invert a chain link whose
 * other end it never mentions, so the handler re-checks the merged
 * before+patch record, not the raw patch (same pattern as
 * `mergedCompetitionRulesSchema` in `competition.ts`).
 */
export const mergedRoundRulesSchema = z
  .object({
    registrationStartAt: utcTimestampSchema.nullable(),
    registrationEndAt: utcTimestampSchema.nullable(),
    submissionStartAt: utcTimestampSchema.nullable(),
    submissionEndAt: utcTimestampSchema.nullable(),
    judgingStartAt: utcTimestampSchema.nullable(),
    judgingEndAt: utcTimestampSchema.nullable(),
    resultPublishAt: utcTimestampSchema.nullable(),
  })
  .superRefine(addRoundScheduleIssues)

export const roundAdvancementSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  fromRoundId: publicIdSchema,
  toRoundId: publicIdSchema,
  teamId: publicIdSchema,
  sourceSubmissionId: publicIdSchema,
  decidedByUserId: publicIdSchema.nullable(),
  note: z.string().max(2000).nullable(),
  createdAt: utcTimestampSchema,
})
export type RoundAdvancement = z.infer<typeof roundAdvancementSchema>

export const roundAdvancementDtoSchema = roundAdvancementSchema.omit({ competitionId: true })
export type RoundAdvancementDto = z.infer<typeof roundAdvancementDtoSchema>

export const createRoundAdvancementsRequestSchema = z.strictObject({
  submissionIds: z.array(publicIdSchema).min(1).max(500),
  note: z.string().max(2000).nullable().default(null),
})
export type CreateRoundAdvancementsRequest = z.infer<typeof createRoundAdvancementsRequestSchema>
